/*
 * Copyright © 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import { InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { translate, translateHelperTextIfExists } from "@/src/services/translations/translations";
import { floatFieldValidator, numberFieldDefault, requiredValidator } from "@/src/services/validationRules";
import React, { ReactNode, useEffect } from "react";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { getFormDefinitions } from "@/src/services/formDefinitions/formDefinitions";
import { ValidatorType } from "@/src/types/ValidatorType";
import { ThousandsNumberInput } from "@/src/components/FormFields/MaskedInputs/ThousandsNumberInput";
import { ThousandsNumberInputFloat } from "@/src/components/FormFields/MaskedInputs/ThousandsNumberInputFloat";
import wizardStyle from "@/src/components/Form/wizardStyle";

type NumberFieldConfiguredProps = {
  caseData: Record<string, any>;
  fieldPath: string;
  required?: boolean | undefined;
  numberType?: "float" | "int";
  fieldType?: "money" | null;
  inputProps?: any;
  gridItemXs?: number;
  customLabel?: string | null;
  customHelperText?: string | null | ReactNode;
  validators?: Array<ValidatorType>;
};
export default function NumberFieldConfigured({
  caseData,
  fieldPath,
  required,
  numberType = "int",
  fieldType = null,
  inputProps = null,
  gridItemXs = 12,
  customLabel = null,
  customHelperText = null,
  validators = [],
}: NumberFieldConfiguredProps) {
  const helperText = customHelperText ?? translateHelperTextIfExists(caseData, fieldPath);
  const question = translate(`${caseData.name}.${fieldPath}.question`, caseData, ""); // "" is the default value here because the netIncomePetitioner and netIncomePartner fields in the divorce form aren't supposed to have headings
  const label = customLabel ?? translate(`${caseData.name}.${fieldPath}.label`, caseData);

  useEffect(() => {
    if (required) {
      validators.push(requiredValidator());
    }
    if (numberType === "int") {
      validators.push(numberFieldDefault);
    }
    if (numberType === "float") {
      validators.push(floatFieldValidator);
    }
    if (getFormDefinitions(caseData, fieldPath) && getFormDefinitions(caseData, fieldPath).validators) {
      const definedValidators: Array<ValidatorType> = getFormDefinitions(caseData, fieldPath).validators;
      definedValidators.forEach((definedValidator) => {
        validators.push(definedValidator);
      });
    }
  }, []);

  const endAdornments = {
    money: "€",
  };

  return (
    <>
      {question && (
        <Grid sx={wizardStyle.gridTopSpaced} size={gridItemXs}>
          <FormLegend>{question}</FormLegend>
        </Grid>
      )}
      <Grid size={gridItemXs}>
        <ValidatorTextField
          name={fieldPath}
          label={label}
          helperText={helperText}
          validators={validators}
          InputProps={{
            inputComponent: numberType === "float" ? ThousandsNumberInputFloat : ThousandsNumberInput,
            startAdornment: !!fieldType ? <InputAdornment position="start">{endAdornments[fieldType]}</InputAdornment> : null,
            ...inputProps,
          }}
        />
      </Grid>
    </>
  );
}
